import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingDown, TrendingUp, Info, Layers, ChevronRight, Sparkles, Coins, AlertTriangle,
  Target, PiggyBank, ChefHat,
} from "lucide-react";
import SafeImage from "../components/SafeImage";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { api } from "../lib/api";
import { eur, pct } from "../lib/format";

const CALC = {
  monthly: {
    title: "Rentabilité brute estimée / mois",
    definition: "Prix de vente HT moins le coût des ingrédients et consommables, sommé sur le mois. Ce montant ne correspond pas au bénéfice net du restaurant (avant salaires, loyers et charges fixes).",
    formula: "Σ (prix_vente_HT − coût_ingrédients) × volume_mensuel",
    period: "Sur le mois en cours (30 jours)",
    hypotheses: "Volumes historiques constants · prix carte inchangés · selon les volumes actuels",
  },
  cost: {
    title: "Coût total des ingrédients / mois",
    definition: "Somme des ingrédients et consommables utilisés pour préparer les portions sur le mois.",
    formula: "Σ (coût_ingrédients × volume_mensuel)",
    period: "Sur le mois en cours (30 jours)",
    hypotheses: "Utilise le prix d'achat de la dernière facture validée",
  },
  threat: {
    title: "Marge à protéger / 30 jours",
    definition: "Perte de rentabilité estimée si les prix d'achat augmentent et qu'aucune action n'est prise.",
    formula: "impact_annuel_estimé ÷ mois_d'ouverture",
    period: "Mois courant",
    hypotheses: "Toutes les hausses détectées sont maintenues · si aucune action n'est prise",
  },
  under: {
    title: "Recettes à optimiser",
    definition: "Recettes dont la rentabilité est inférieure à l'objectif défini par le restaurateur.",
    formula: "count(recettes où rentabilité_% < objectif_%)",
    period: "État courant",
    hypotheses: "Objectif défini par recette dans son formulaire",
  },
  save: {
    title: "Gain potentiel / an",
    definition: "Montant qui pourrait être récupéré grâce aux actions simulées. Cette estimation dépend des volumes et hypothèses utilisés.",
    formula: "Σ (gain_annuel_estimé_€) de toutes les baisses fournisseur détectées",
    period: "Sur 12 mois d'ouverture",
    hypotheses: "La baisse est confirmée sur une facture validée · estimation, pas un gain garanti",
  },
};

// Palette chaleureuse — couleurs saturées pour la lisibilité
const CAT_PALETTE = [
  { fill: "#0f766e", ring: "ring-teal-200", bg: "bg-teal-50", text: "text-teal-800", bar: "bg-teal-600" },
  { fill: "#c2410c", ring: "ring-orange-200", bg: "bg-orange-50", text: "text-orange-800", bar: "bg-orange-600" },
  { fill: "#a16207", ring: "ring-amber-200", bg: "bg-amber-50", text: "text-amber-800", bar: "bg-amber-600" },
  { fill: "#7c3aed", ring: "ring-violet-200", bg: "bg-violet-50", text: "text-violet-800", bar: "bg-violet-600" },
  { fill: "#0369a1", ring: "ring-sky-200", bg: "bg-sky-50", text: "text-sky-800", bar: "bg-sky-600" },
  { fill: "#be123c", ring: "ring-rose-200", bg: "bg-rose-50", text: "text-rose-800", bar: "bg-rose-600" },
  { fill: "#4d7c0f", ring: "ring-lime-200", bg: "bg-lime-50", text: "text-lime-800", bar: "bg-lime-600" },
  { fill: "#7e22ce", ring: "ring-purple-200", bg: "bg-purple-50", text: "text-purple-800", bar: "bg-purple-600" },
];

function KPI({ icon: Icon, label, value, delta, deltaTone = "muted", note, testid, onExplain, accent }) {
  const deltaClass = {
    good: "text-[#1769E0]",
    bad: "text-rose-700",
    warn: "text-orange-700",
    muted: "text-slate-500",
  }[deltaTone];
  const accents = {
    teal:    { icon: "bg-teal-600 text-white",       tint: "bg-teal-50/60",     bar: "bg-teal-600",     value: "text-teal-900" },
    orange:  { icon: "bg-orange-600 text-white",     tint: "bg-orange-50/60",   bar: "bg-orange-600",   value: "text-orange-900" },
    rose:    { icon: "bg-rose-600 text-white",       tint: "bg-rose-50/60",     bar: "bg-rose-600",     value: "text-rose-900" },
    amber:   { icon: "bg-amber-500 text-white",      tint: "bg-amber-50/60",    bar: "bg-amber-500",    value: "text-amber-900" },
    emerald: { icon: "bg-[#1769E0] text-white",    tint: "bg-[#DCEBFF]/60",  bar: "bg-[#1769E0]",  value: "text-[#0B1F33]" },
  }[accent] || { icon: "bg-slate-700 text-white", tint: "bg-slate-50", bar: "bg-slate-700", value: "text-slate-900" };
  const TrendIcon = deltaTone === "good" ? TrendingUp : deltaTone === "bad" ? TrendingDown : null;
  return (
    <div className="relative bg-white rounded-xl border border-slate-200 overflow-hidden group hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={testid}>
      <div className={`absolute inset-x-0 top-0 h-1 ${accents.bar}`} />
      <div className={`absolute -right-8 -top-8 w-24 h-24 rounded-full ${accents.tint} opacity-70`} />
      <div className="relative p-4">
        <div className="flex items-start justify-between gap-2">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm ${accents.icon}`}>
            <Icon className="w-5 h-5" strokeWidth={2.25} />
          </div>
          {onExplain && (
            <button onClick={onExplain} className="text-slate-400 hover:text-slate-900 transition-colors" title="Voir le calcul" data-testid={`${testid}-explain`} aria-label="Voir le calcul">
              <Info className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="mt-3 text-[10.5px] uppercase tracking-[0.08em] text-slate-500 font-semibold">{label}</div>
        <div className={`mt-0.5 font-mono-num text-[26px] leading-tight font-bold tabular-nums ${accents.value}`}>{value}</div>
        {delta && (
          <div className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-mono-num font-medium ${deltaClass}`}>
            {TrendIcon && <TrendIcon className="w-3 h-3" strokeWidth={2.5} />}
            {delta}
          </div>
        )}
        {note && <div className="mt-1 text-[11px] text-slate-500 leading-tight">{note}</div>}
      </div>
    </div>
  );
}

// SVG Donut chart
function DonutChart({ data, total, size = 220, thickness = 32, onSlice }) {
  const radius = (size - thickness) / 2;
  const cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const slices = data.map((d, i) => {
    const frac = total > 0 ? d.value / total : 0;
    const dash = frac * circumference;
    const seg = {
      key: d.label,
      color: CAT_PALETTE[i % CAT_PALETTE.length].fill,
      dash, gap: circumference - dash, offset,
      data: d,
    };
    offset += dash;
    return seg;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={thickness} />
      {slices.map((s, i) => (
        <circle
          key={s.key}
          cx={cx} cy={cy} r={radius}
          fill="none"
          stroke={s.color}
          strokeWidth={thickness}
          strokeDasharray={`${s.dash} ${s.gap}`}
          strokeDashoffset={-s.offset}
          transform={`rotate(-90 ${cx} ${cy})`}
          className="cursor-pointer transition-opacity hover:opacity-80"
          onClick={() => onSlice && onSlice(s.data.raw)}
          data-testid={`donut-slice-${s.data.raw?.category || i}`}
        />
      ))}
    </svg>
  );
}

function LeakBar({ cat, mode, max, onDrill, color }) {
  const value = mode === "surcout" ? cat.total_diff : cat.spent_current;
  const width = Math.max(3, Math.min(100, (Math.abs(value) / Math.max(1, max)) * 100));
  const tone = mode === "surcout"
    ? (cat.total_diff > 0 ? "bg-rose-600" : cat.total_diff < 0 ? "bg-[#2878FF]" : "bg-slate-400")
    : color;
  const dv = cat.variation_pct;
  const dvClass = dv > 0 ? "text-rose-700" : dv < 0 ? "text-[#1769E0]" : "text-slate-500";
  return (
    <button onClick={onDrill} className="w-full text-left group hover:bg-slate-50 rounded-lg px-2 py-2 transition-colors" data-testid={`leak-${cat.category}`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-slate-900 truncate">{cat.category}</span>
          <span className="text-[11px] text-slate-500 font-mono-num shrink-0">{cat.product_count} produits</span>
        </div>
        <div className="flex items-baseline gap-3 shrink-0">
          <span className={`text-[11px] font-mono-num ${dvClass}`}>{dv > 0 ? "+" : ""}{dv.toFixed(1)} %</span>
          <span className="font-mono-num text-sm font-semibold text-slate-900 tabular-nums">{eur(value)}</span>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700" />
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${tone} transition-all`} style={{ width: `${width}%` }} />
        </div>
        <span className="text-[10px] text-slate-400 font-mono-num tabular-nums w-10 text-right">{cat.share_pct}%</span>
      </div>
    </button>
  );
}

function greet() {
  const h = new Date().getHours();
  if (h < 5) return "Bonne nuit";
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bel après-midi";
  return "Bonsoir";
}
function friendlyLine() {
  const lines = [
    "Voici où en est votre marge aujourd'hui.",
    "Un coup d'œil rapide sur votre cuisine.",
    "On regarde ensemble où fuit votre argent ?",
    "Vos chiffres, votre marge, en un clin d'œil.",
  ];
  return lines[new Date().getDate() % lines.length];
}

export default function Today() {
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [leaks, setLeaks] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calcFor, setCalcFor] = useState(null);
  const [leakMode, setLeakMode] = useState("depenses");
  const [drillCat, setDrillCat] = useState(null);
  const [drillProduct, setDrillProduct] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [b, l, s, prods] = await Promise.all([
        api.todayBrief(),
        api.leaks(),
        api.settings().catch(() => null),
        api.products().catch(() => []),
      ]);
      // Attach the trend-enriched product list to `leaks` so the hausse/baisse/stable
      // cards read counts directly from the catalog (single source of truth).
      setBrief(b); setLeaks({ ...l, _products_trend: prods }); setSettings(s);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Dashboard Live — le brief se recharge automatiquement après validation
  // facture, changement de produit / recette / prix / objectif.
  useEffect(() => {
    const refresh = () => load();
    const evs = [
      "rentascan:invoice-validated",
      "rentascan:usage-changed",
      "rentascan:recipe-updated",
      "rentascan:product-updated",
    ];
    evs.forEach((e) => window.addEventListener(e, refresh));
    return () => evs.forEach((e) => window.removeEventListener(e, refresh));
  }, []);

  const sortedCats = useMemo(() => {
    if (!leaks?.categories) return [];
    if (leakMode === "surcout") return [...leaks.categories].sort((a, b) => b.total_diff - a.total_diff);
    return [...leaks.categories].sort((a, b) => b.spent_current - a.spent_current);
  }, [leaks, leakMode]);
  const maxVal = useMemo(() => {
    if (!sortedCats.length) return 1;
    return leakMode === "surcout"
      ? Math.max(...sortedCats.map((c) => Math.abs(c.total_diff)))
      : Math.max(...sortedCats.map((c) => c.spent_current));
  }, [sortedCats, leakMode]);

  const donutData = useMemo(() => {
    if (!leaks?.categories) return { data: [], total: 0 };
    const items = [...leaks.categories]
      .sort((a, b) => b.spent_current - a.spent_current)
      .map((c) => ({ label: c.category, value: Math.max(0, c.spent_current), raw: c }));
    const total = items.reduce((s, i) => s + i.value, 0);
    return { data: items, total };
  }, [leaks]);

  if (loading || !brief || !leaks) {
    return (
      <div className="space-y-6" data-testid="today-page-loading">
        <div className="h-24 skeleton rounded-2xl" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-28 skeleton rounded-xl" />)}
        </div>
        <div className="h-72 skeleton rounded-xl" />
      </div>
    );
  }
  const k = brief.kpis;
  const coutMatiere = Math.max(0, (leaks.total_spent_current || 0));
  const projSign = leaks.annual_projection >= 0 ? "+" : "-";
  const projTone = leaks.annual_projection > 0 ? "bad" : leaks.annual_projection < 0 ? "good" : "muted";
  const estab = settings?.establishment_name || "";

  return (
    <div className="space-y-10" data-testid="today-page">
      {/* Editorial banner — dark navy full width */}
      <section className="relative overflow-hidden rounded-2xl" data-testid="today-hero">
        <div className="bg-[#0B1F33] px-8 lg:px-12 py-10 lg:py-12 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-end">
          <div>
            <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] font-bold text-[#FFD166]">
              <Sparkles className="w-3.5 h-3.5" /> {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <h1 className="mt-4 font-heading text-white tracking-tight leading-[1.02]" style={{ fontSize: "clamp(28px, 4vw, 46px)", fontWeight: 800, letterSpacing: "-0.02em" }}>
              {greet()}{estab ? <>,<br /><span className="marker-solid">{estab}</span></> : " chef"}
            </h1>
            <p className="mt-4 text-[15px] text-white/70 max-w-lg">Un coup d'œil rapide à vos chiffres.</p>
          </div>

          {/* Big KPI right */}
          {brief.month_impact !== undefined && (
            <div className="lg:text-right lg:border-l lg:border-white/10 lg:pl-8">
              <div className="text-[10.5px] uppercase tracking-widest font-bold text-white/60">Impact ce mois</div>
              <div className="mt-2 text-[64px] font-extrabold tabular-nums text-[#FF9F1C] leading-none">{brief.month_impact > 0 ? "+" : ""}{eur(brief.month_impact || 0)}</div>
              <div className="mt-1 text-[13px] text-white/60">écart vs mois précédent</div>
            </div>
          )}
        </div>
        {/* Editorial band */}
        <div className="h-[3px] flex">
          <span className="w-32 bg-[#FF9F1C]" />
          <span className="flex-1 bg-[#1769E0]" />
        </div>
      </section>

      {/* Évolution des prix — hausses & baisses (le cœur de l'info) */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="price-movements">
        {(() => {
          const trended = (leaks._products_trend || []).filter((p) => p.trend);
          const hausses = trended.filter((p) => p.trend.status === "up").sort((a, b) => (b.trend.variation_pct || 0) - (a.trend.variation_pct || 0));
          const baisses = trended.filter((p) => p.trend.status === "down").sort((a, b) => (a.trend.variation_pct || 0) - (b.trend.variation_pct || 0));
          // Feb 2026 §« libellés honnêtes » — Le comparatif porte toujours
          // sur DEUX achats successifs par produit, jamais sur un mois
          // calendaire complet. Afficher « septembre comparé à août »
          // était trompeur : la démo ne contient que des achats 04/08 et
          // 19/08. Le libellé exprime maintenant l'objet réel du calcul.
          const periodLabel = "Derniers achats comparés aux précédents";
          const Card = ({ title, count, list, tone, filterKey, testid }) => {
            const barColor = tone === "up" ? "#D84343" : "#24845B";
            const top5 = list.slice(0, 5);
            return (
              <button
                onClick={() => (window.location.href = `/produits?filter=${filterKey}`)}
                className="text-left bg-white rounded-2xl border border-[#CDD7E3] overflow-hidden hover:border-[#0B1F33] hover:shadow-[0_14px_36px_rgba(11,31,51,0.08)] transition-all flex flex-col h-full"
                data-testid={testid}
              >
                <div className="px-5 py-4 flex items-center gap-3 border-b border-[#CDD7E3]">
                  <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ backgroundColor: barColor }}>
                    {tone === "up" ? <TrendingUp className="w-5 h-5" strokeWidth={2.5} /> : <TrendingDown className="w-5 h-5" strokeWidth={2.5} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[16px] font-extrabold text-[#0B1F33]">{title}</div>
                    <div className="text-[11.5px] text-[#5E6875]">{periodLabel} · top 5</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[28px] font-extrabold tabular-nums leading-none" style={{ color: barColor }} data-testid={`${testid}-count`}>{count}</div>
                  </div>
                </div>
                <div className="py-1 flex-1">
                  {top5.length === 0 ? (
                    <div className="text-sm text-slate-500 italic py-8 text-center">Aucun produit dans cette catégorie.</div>
                  ) : top5.map((p) => (
                    <div key={p.id} className="px-4 py-2 border-b border-slate-100 last:border-b-0 flex items-baseline justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium text-[#0B1F33] truncate">{p.name}</div>
                        <div className="text-[11px] text-[#5E6875] truncate">{p.category}{p.trend.curr_supplier_name ? ` · ${p.trend.curr_supplier_name}` : ""}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[13px] font-mono-num font-bold tabular-nums" style={{ color: barColor }}>
                          {p.trend.variation_pct > 0 ? "+" : ""}{p.trend.variation_pct.toFixed(1)} %
                        </div>
                        <div className="text-[10.5px] font-mono-num text-[#5E6875] tabular-nums">
                          {p.trend.prev_price?.toFixed(2)} → {p.trend.curr_price?.toFixed(2)} {p.trend.unit}
                        </div>
                      </div>
                    </div>
                  ))}
                  {list.length > 5 && (
                    <div className="px-4 py-1.5 text-[11px] text-[#5E6875] italic">+ {list.length - 5} autres — cliquez pour tout voir</div>
                  )}
                </div>
                <div className="mt-auto px-4 py-2 border-t border-[#CDD7E3] bg-[#F4F5EF] text-[11.5px] font-semibold text-[#0B1F33] flex items-center gap-1">
                  Voir la liste filtrée <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </button>
            );
          };
          return (
            <>
              <Card title="Produits en hausse" count={hausses.length} list={hausses} tone="up"   filterKey="up"   testid="price-hausses" />
              <Card title="Produits en baisse" count={baisses.length} list={baisses} tone="down" filterKey="down" testid="price-baisses" />
            </>
          );
        })()}
      </section>

      {/* Recettes nécessitant une décision — deux sections toujours
          visibles : Recettes à optimiser (rentabilité < objectif) +
          Recettes avec excédent de marge. Cartes cliquables reliées aux
          vraies fiches recettes via /plats?edit=<id>. */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="recipes-decisions">
        <TodayRecipesCard
          title="Recettes à optimiser"
          icon={<AlertTriangle className="w-4 h-4 text-orange-600" />}
          testid="today-optimize"
          rows={brief.recipes_to_optimize || []}
          emptyLabel="Aucune recette à optimiser actuellement."
          renderRow={(r) => (
            <button
              type="button"
              onClick={() => navigate(`/plats?edit=${r.id}&from=today`)}
              data-testid={`today-optimize-row-${r.id}`}
              className="w-full text-left py-3 flex items-center gap-3 hover:bg-orange-50/60 active:bg-orange-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9F1C] focus-visible:ring-inset rounded-md -mx-2 px-2 cursor-pointer transition-colors"
            >
              <SafeImage src={r.photo_url} alt={r.name} className="w-14 h-14 rounded-md shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-slate-900 truncate">{r.name}</div>
                <div className="text-[11.5px] text-slate-600 mt-0.5 truncate">
                  {r.up_count > 1 ? (
                    <><span className="font-semibold text-orange-700">{r.up_count} produits en hausse</span> · dont {(r.ingredients_up[0] || {}).product_name}</>
                  ) : r.up_count === 1 ? (
                    <><span className="font-semibold text-orange-700">{(r.ingredients_up[0] || {}).product_name}</span> {eur((r.ingredients_up[0] || {}).previous_price_ht)} → <span className="font-bold text-slate-900">{eur((r.ingredients_up[0] || {}).current_price_ht)}</span> <span className="text-red-700 font-semibold">(+{pct((r.ingredients_up[0] || {}).variation_pct)})</span></>
                  ) : (
                    <span className="font-semibold text-red-700" data-testid={`today-under-target-label-${r.id}`}>Rentabilité inférieure à l'objectif</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 font-mono-num tabular-nums">
                  Coût {eur(r.previous_cost_per_portion, 3)} → <span className="font-bold text-slate-900">{eur(r.current_cost_per_portion, 3)}</span>
                  <span className="mx-1">·</span>
                  Marge <span className={r.under_target ? "text-red-700 font-bold" : "text-slate-900 font-semibold"}>{pct(r.matter_margin_pct)}</span>
                  {r.target_margin_pct != null && <> / obj. {pct(r.target_margin_pct)}</>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <TodayShortfallBadge r={r} />
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" aria-hidden />
            </button>
          )}
        />
        <TodayRecipesCard
          title="Recettes avec excédent de marge"
          icon={<Sparkles className="w-4 h-4 text-[#24845B]" />}
          testid="today-surplus"
          rows={brief.recipes_over_target || []}
          emptyLabel="Aucune recette en excédent pour l'instant."
          renderRow={(r) => (
            <button
              type="button"
              onClick={() => navigate(`/plats?edit=${r.id}&from=today`)}
              data-testid={`today-surplus-row-${r.id}`}
              className="w-full text-left py-3 flex items-center gap-3 hover:bg-[#DCEBFF]/40 active:bg-[#DCEBFF]/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24845B] focus-visible:ring-inset rounded-md -mx-2 px-2 cursor-pointer transition-colors"
            >
              <SafeImage src={r.photo_url} alt={r.name} className="w-14 h-14 rounded-md shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-slate-900 truncate">{r.name}</div>
                <div className="text-[11.5px] text-[#24845B] font-semibold mt-0.5 inline-flex items-center gap-1">
                  <Target className="w-3 h-3" /> Rentabilité supérieure à l'objectif
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 font-mono-num tabular-nums">
                  Marge <span className="font-bold text-slate-900">{pct(r.matter_margin_pct)}</span> / obj. {pct(r.target_margin_pct)}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono-num text-[12.5px] font-extrabold text-[#24845B] tabular-nums">+{r.surplus_pts} %</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" aria-hidden />
            </button>
          )}
        />
      </section>

      {/* Calc explanation dialog */}
      <Dialog open={!!calcFor} onOpenChange={(v) => !v && setCalcFor(null)}>
        <DialogContent data-testid="calc-dialog">
          {calcFor === "leaks" ? (
            <>
              <DialogHeader><DialogTitle>Carte des fuites de marge — méthode</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm text-slate-700">
                <div><span className="text-slate-400 uppercase text-[10px] tracking-wider mr-1">Formules</span>
                  <div className="font-mono-num text-xs bg-slate-50 p-2 rounded mt-1">
                    effet_prix = (prix_actuel − prix_précédent) × qté_actuelle<br />
                    effet_volume = (qté_actuelle − qté_précédente) × prix_précédent<br />
                    écart_total = dépenses_actuelles − dépenses_précédentes
                  </div>
                </div>
                <div><span className="text-slate-400 uppercase text-[10px] tracking-wider mr-1">Période courante</span> {leaks.current_period}</div>
                <div><span className="text-slate-400 uppercase text-[10px] tracking-wider mr-1">Baseline</span> {leaks.reference_period}</div>
                <div><span className="text-slate-400 uppercase text-[10px] tracking-wider mr-1">Effet volume</span> {leaks.volume_comparison_available ? "Comparaison disponible" : "Comparaison indisponible — pas d'historique de volume produit."}</div>
                <div className="text-[11px] text-slate-500 mt-2">Mise à jour {new Date(leaks.last_updated).toLocaleString("fr-FR")}</div>
              </div>
            </>
          ) : calcFor && CALC[calcFor] ? (
            <>
              <DialogHeader><DialogTitle>{CALC[calcFor].title}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm text-slate-700">
                {CALC[calcFor].definition && (
                  <div className="bg-[#DCEBFF] border border-[#DCEBFF] rounded-md p-3 text-[13px] leading-snug text-slate-800">
                    {CALC[calcFor].definition}
                  </div>
                )}
                <div><span className="text-slate-400 uppercase text-[10px] tracking-wider mr-1">Formule</span>
                  <div className="font-mono-num text-xs bg-slate-50 p-2 rounded mt-1">{CALC[calcFor].formula}</div></div>
                <div><span className="text-slate-400 uppercase text-[10px] tracking-wider mr-1">Période</span> {CALC[calcFor].period}</div>
                <div><span className="text-slate-400 uppercase text-[10px] tracking-wider mr-1">Hypothèses</span> {CALC[calcFor].hypotheses}</div>
                <div className="text-[11px] text-slate-500 mt-2">Mise à jour à chaque ouverture depuis les données fournisseur validées. Estimation — dépend des volumes actuels.</div>
              </div>
            </>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalcFor(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drill-down dialog: category → products */}
      <Dialog open={!!drillCat} onOpenChange={(v) => !v && setDrillCat(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="drill-dialog">
          {drillCat && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-slate-900">
                  <Layers className="w-4 h-4 text-[#0B1F33]" /> {drillCat.category}
                  <span className="text-xs font-normal text-slate-500 ml-2">{drillCat.product_count} produits · {eur(drillCat.spent_current)}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-3 mb-3 text-[11px]">
                <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2">
                  <div className="text-slate-500 uppercase tracking-wider">Effet prix</div>
                  <div className={`font-mono-num text-sm font-semibold ${drillCat.price_effect > 0 ? "text-rose-700" : "text-[#1769E0]"}`}>{drillCat.price_effect > 0 ? "+" : ""}{eur(drillCat.price_effect)}</div>
                </div>
                <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2">
                  <div className="text-slate-500 uppercase tracking-wider">Effet volume</div>
                  <div className="font-mono-num text-sm text-slate-500">indisponible</div>
                </div>
                <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2">
                  <div className="text-slate-500 uppercase tracking-wider">Impact annuel projeté</div>
                  <div className={`font-mono-num text-sm font-semibold ${drillCat.annual_impact > 0 ? "text-rose-700" : "text-[#1769E0]"}`}>{drillCat.annual_impact > 0 ? "+" : ""}{eur(drillCat.annual_impact)}</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="text-left font-medium py-2">Produit</th>
                      <th className="text-left font-medium py-2">Fournisseur</th>
                      <th className="text-right font-medium py-2">Prix actuel</th>
                      <th className="text-right font-medium py-2">Prix ancien</th>
                      <th className="text-right font-medium py-2">Var.</th>
                      <th className="text-right font-medium py-2">Effet prix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drillCat.products.map((p) => (
                      <tr key={p.product_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDrillProduct(p)} data-testid={`drill-product-${p.product_id}`}>
                        <td className="py-2 text-slate-900">{p.product_name}</td>
                        <td className="py-2 text-slate-600 text-[12px]">{p.supplier_name}</td>
                        <td className="py-2 text-right font-mono-num text-slate-900">{p.cost_current.toFixed(4)} €/{p.unit_base}</td>
                        <td className="py-2 text-right font-mono-num text-slate-500">{p.cost_previous.toFixed(4)} €/{p.unit_base}</td>
                        <td className={`py-2 text-right font-mono-num ${p.variation_pct > 0 ? "text-rose-700" : p.variation_pct < 0 ? "text-[#1769E0]" : "text-slate-500"}`}>{p.variation_pct > 0 ? "+" : ""}{p.variation_pct.toFixed(1)} %</td>
                        <td className={`py-2 text-right font-mono-num ${p.price_effect > 0 ? "text-rose-700" : p.price_effect < 0 ? "text-[#1769E0]" : "text-slate-500"}`}>{p.price_effect > 0 ? "+" : ""}{eur(p.price_effect)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrillCat(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product detail dialog */}
      <Dialog open={!!drillProduct} onOpenChange={(v) => !v && setDrillProduct(null)}>
        <DialogContent data-testid="drill-product-dialog">
          {drillProduct && (
            <>
              <DialogHeader><DialogTitle>{drillProduct.product_name}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="text-slate-600 text-[12px]">Fournisseur : <span className="text-slate-900">{drillProduct.supplier_name}</span></div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2">
                    <div className="text-slate-500 uppercase tracking-wider text-[10px]">Volume utilisé / mois</div>
                    <div className="font-mono-num text-sm text-slate-900">{drillProduct.qty_base} {drillProduct.unit_base}</div>
                  </div>
                  <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2">
                    <div className="text-slate-500 uppercase tracking-wider text-[10px]">Dépense mensuelle</div>
                    <div className="font-mono-num text-sm text-slate-900">{eur(drillProduct.spent_current)}</div>
                  </div>
                  <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2">
                    <div className="text-slate-500 uppercase tracking-wider text-[10px]">Prix actuel</div>
                    <div className="font-mono-num text-sm text-slate-900">{drillProduct.cost_current.toFixed(4)} €/{drillProduct.unit_base}</div>
                  </div>
                  <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2">
                    <div className="text-slate-500 uppercase tracking-wider text-[10px]">Prix précédent</div>
                    <div className="font-mono-num text-sm text-slate-500">{drillProduct.cost_previous.toFixed(4)} €/{drillProduct.unit_base}</div>
                  </div>
                </div>
                <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2 mt-2">
                  <div className="text-slate-500 uppercase tracking-wider text-[10px]">Effet prix mensuel</div>
                  <div className={`font-mono-num text-base font-semibold ${drillProduct.price_effect > 0 ? "text-rose-700" : "text-[#1769E0]"}`}>{drillProduct.price_effect > 0 ? "+" : ""}{eur(drillProduct.price_effect)}</div>
                  <div className="text-[11px] text-slate-500 mt-1">= ({drillProduct.cost_current.toFixed(4)} − {drillProduct.cost_previous.toFixed(4)}) × {drillProduct.qty_base} {drillProduct.unit_base}</div>
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrillProduct(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Carte Section « Recettes à optimiser » / « Excédent de marge » ----
// Affichée en permanence même quand la liste est vide (message obligatoire).
function TodayRecipesCard({ title, icon, testid, rows, emptyLabel, renderRow }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 5);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
      <h2 className="font-heading text-lg text-slate-900 flex items-center gap-2 mb-3">
        {icon} {title}
        <span className="text-[11px] font-mono-num text-slate-500 tabular-nums">({rows.length})</span>
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic" data-testid={`${testid}-empty`}>{emptyLabel}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {visible.map((r) => <li key={r.id}>{renderRow(r)}</li>)}
        </ul>
      )}
      {rows.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`${testid}-toggle`}
          className="mt-3 h-8 px-3 rounded-md text-[12px] font-semibold text-[#1769E0] hover:bg-[#DCEBFF]/40"
        >
          {expanded ? "Réduire la liste" : `Voir les ${rows.length} recettes`}
        </button>
      )}
    </div>
  );
}


/**
 * Feb 2026 — « Manque à gagner » sur « Recettes à optimiser ».
 * Formule commune fiche recette ↔ tableau de bord ↔ page Today.
 *   coût cible = prix vente HT × (1 − objectif/100)
 *   écart / portion = max(0 ; coût réel − coût cible)
 *   annuel = écart × volume mensuel × mois ouverts
 * Le backend gère l'arrondi ; le front n'affiche jamais « -0 € ».
 */
function TodayShortfallBadge({ r }) {
  const perPortion = r.shortfall_per_portion_eur;
  const annual = r.annual_shortfall_eur;
  const note = r.shortfall_note;

  if (note === "missing_inputs") {
    return <div className="font-mono-num text-[11px] font-bold text-[#5E6875]" data-testid={`shortfall-missing-${r.id}`}>Coût ou prix manquant</div>;
  }
  if (perPortion == null || perPortion <= 0) {
    return <div className="font-mono-num text-[11px] font-bold text-[#24845B]" data-testid={`shortfall-ok-${r.id}`}>Objectif atteint</div>;
  }
  const perPortionLabel = perPortion < 0.005 ? "< 0,01 €" : eur(perPortion, 2);
  if (annual == null) {
    return (
      <div className="text-right" data-testid={`shortfall-portion-only-${r.id}`}>
        <div className="font-mono-num text-[12.5px] font-extrabold text-orange-700 tabular-nums">-{perPortionLabel}/portion</div>
        <div className="text-[10px] text-[#5E6875]">Vol. non renseigné</div>
      </div>
    );
  }
  if (annual === 0) {
    return (
      <div className="text-right" data-testid={`shortfall-portion-only-${r.id}`}>
        <div className="font-mono-num text-[12.5px] font-extrabold text-orange-700 tabular-nums">-{perPortionLabel}/portion</div>
        <div className="text-[10px] text-[#5E6875]">Volume = 0</div>
      </div>
    );
  }
  return (
    <div className="text-right" data-testid={`shortfall-annual-${r.id}`}>
      <div className="font-mono-num text-[12.5px] font-extrabold text-red-700 tabular-nums">-{eur(annual)}/an</div>
      <div className="text-[10px] text-[#5E6875]">-{perPortionLabel}/portion</div>
    </div>
  );
}
